import { filter, forEach, isEmpty, isNumber, map, times } from 'es-toolkit/compat';
import { AsyncBuffer, byteLengthFromUrl } from 'hyparquet';
import pLimit from 'p-limit';

import { getParquetResourceId, measureParquetStage } from './instrumentation';

const rangeRequestLimit = pLimit(5);

export const defaultParquetRangeWindowSize = 1 << 23;

export async function byteLengthFromUrlWithGet(url: string) {
  return fetch(url, { method: 'GET' }).then((response) => {
    if (!response.ok) throw new Error(`Failed to get parquet byte length: ${response.status}`);
    const length = response.headers.get('Content-Length');
    if (!length) throw new Error('Parquet response is missing its content length');
    return parseInt(length);
  });
}

const fetchUrlSlice = (url: string, start: number, end: number | undefined, failOnError: boolean = false) =>
  rangeRequestLimit(async () => {
    const headers = new Headers();
    const endString = end === undefined ? '' : end - 1;
    headers.set('Range', `bytes=${start}-${endString}`);
    const resourceId = getParquetResourceId(url);
    try {
      const response = await fetch(url, { headers });
      if (!response.ok || !response.body) {
        const errorMessage = `Failed to fetch parquet byte range ${resourceId}: ${response.status}`;
        if (failOnError) throw new Error(errorMessage);
        return new ArrayBuffer(0);
      }
      return await response.arrayBuffer();
    } catch {
      if (failOnError) {
        throw new Error(`Failed to fetch parquet byte range ${resourceId}`);
      }
      return new ArrayBuffer(0);
    }
  });

export async function asyncBufferFromUrlWithGetFallback(url: string, byteLength?: number): Promise<AsyncBuffer> {
  if (!byteLength) {
    try {
      byteLength = await byteLengthFromUrl(url);
      if (!isNumber(byteLength) || byteLength <= 1) {
        byteLength = await byteLengthFromUrlWithGet(url);
      }
    } catch {
      byteLength = await byteLengthFromUrlWithGet(url);
    }
  }

  return {
    byteLength,
    slice: async (start: number, end?: number) => fetchUrlSlice(url, start, end),
  };
}

export function cachedAsyncBufferWithFixedWindows(
  { byteLength, slice }: AsyncBuffer,
  {
    minSize = defaultParquetRangeWindowSize,
    resourceId = 'parquet-unknown',
    thread = 'main',
  }: {
    minSize?: number;
    resourceId?: string;
    thread?: 'main' | 'worker';
  } = {}
): AsyncBuffer {
  if (byteLength < minSize) {
    const buffer = slice(0, byteLength);
    return {
      byteLength,
      async slice(start: number, end: number) {
        return (await buffer).slice(start, end);
      },
    };
  }
  const numberOfCacheEntries = Math.ceil(byteLength / minSize);
  const cache = new Map<string, Promise<ArrayBuffer> | ArrayBuffer>();
  const allCacheAddresses = map(times(numberOfCacheEntries), (index) => {
    const start = index * minSize;
    const end = Math.min(start + minSize, byteLength);
    return { start, end };
  });
  return {
    byteLength,
    async slice(start: number, end: number | undefined): Promise<ArrayBuffer> {
      const relevantCacheAddresses = filter(allCacheAddresses, (address) => {
        return (
          (start >= address.start && start < address.end) ||
          (end !== undefined && end > address.start && end <= address.end) ||
          (start < address.start && (end === undefined || end > address.end))
        );
      });
      if (isEmpty(relevantCacheAddresses)) return new ArrayBuffer(0);

      const requestedSlices = await Promise.all(
        map(relevantCacheAddresses, async ({ start: sliceStart, end: sliceEnd }) => {
          const cacheKey = `${sliceStart}-${sliceEnd}`;
          const cachedSlice = cache.get(cacheKey);
          if (cachedSlice) return cachedSlice;
          if (sliceStart >= byteLength) return new ArrayBuffer(0);

          const requestedSlice = slice(sliceStart, Math.min(sliceEnd, byteLength));
          cache.set(cacheKey, requestedSlice);
          return requestedSlice;
        })
      );
      return measureParquetStage({ resourceId, stage: 'range-buffer-copy', thread }, () => {
        const totalLength = (end ?? byteLength) - start;
        const concatenatedBuffer = new ArrayBuffer(totalLength);
        const concatenatedBufferView = new Uint8Array(concatenatedBuffer);

        forEach(requestedSlices, (requestedSlice, sliceIndex) => {
          const { start: sliceStart, end: sliceEnd } = relevantCacheAddresses[sliceIndex];
          const windowCopyStart = Math.max(start, sliceStart);
          const windowCopyEnd = Math.min(end ?? byteLength, sliceEnd);
          const copyLength = windowCopyEnd - windowCopyStart;
          if (copyLength <= 0) return;

          concatenatedBufferView.set(
            new Uint8Array(requestedSlice, windowCopyStart - sliceStart, copyLength),
            windowCopyStart - start
          );
        });
        return concatenatedBuffer;
      });
    },
  };
}
